import {
  ui,
  residents,
  user,
  accounts,
  alerts,
  alertFilters,
  alertCounts,
} from './store.js';
import { 
  upsertResident,
  loadResidents,
  upsertAccount,
  loadAccounts,
  loadAlerts,
  deleteUserAccount,
  deleteResidentAccounts,
  deleteResident,
  loadReportAlerts,
  loadAlertAreas } from './firebase/db.js';
import { 
  stickyToast,
  clearToasts,
  toast
 } from './components/Toast.svelte';
import { showLoading } from './components/Overlay.svelte';
import { Role } from './constants.js';
import { get } from 'svelte/store';
import { AlertService } from './services/alert-service';
import moment from 'moment';
import { 
  addRemainingAlerts,
  stitchAlertsWithAccount } from './util';
import { FileUploadService } from './services/file-upload-service';
import { LoginService } from './services/login-service';
import { HttpService } from './services/http-service';
import { API_BASE_URL } from './constants.js';

export const AppController = {

  saveResident: (updateResident) => {
    upsertResident(updateResident);
    const residentsCopy = Object.assign(get(residents));
    const residentIndex = residentsCopy.findIndex(resident => resident.resident_id === updateResident.resident_id);
    residentIndex !== -1 ? 
      residentsCopy[residentIndex] = updateResident :
      residentsCopy.push(updateResident);
    residents.set(residentsCopy);
  },

  deleteResident: async (resident) =>  {
    await deleteResident(resident);
    residents.update(current => [...current.filter(currentResident => currentResident.resident_id !== resident.resident_id)]);
  },

  getResident: (resident_id) => {
    return get(residents).find(resident => resident.resident_id === resident_id)
  },

  getResidents: async () => {
    if (!get(residents).length) {
      const data = await loadResidents();
      // set all residents to unselected initially
      const selected = data.map(res => ({...res, selected: false}));
      residents.set(selected);
    }
    return get(residents);
  },

  saveUserSettings: (updatedUser) => {
    const {token, ...updatedWithoutToken} = updatedUser;
    upsertAccount({...updatedWithoutToken, expires: null });
    user.set(updatedUser);
  },

  setUser: (updated) => {
    user.set(updated);
  },

  getUser: () => {
    return { ...get(user) };
  },

  getAccount: (account_id) => {
    return get(accounts).find(account => account.account_id === account_id);
  },

  saveAccount: async (updatedAccount) => {
    await upsertAccount({...updatedAccount, expires: null });
    const accountsCopy = Object.assign(get(accounts));
    const accountIndex = accountsCopy.findIndex(account => account.account_id === updatedAccount.account_id);
    accountIndex !== -1 ?
      accountsCopy[accountIndex] = updatedAccount :
      accountsCopy.push(updatedAccount);
    accounts.set(accountsCopy);
  },

   sendAlert: (alert, isTest) => {
    const { send, ...alertService } = AlertService();
    return send(alert, isTest);
  },

  getAccounts: async () => {
    if(!get(accounts).length) {
      accounts.set(await loadAccounts());
    }
  },

  getAlerts: async () => {
    if(!get(alerts).length) {
      alerts.set(await loadAlerts());
    }
  },

  deleteAccountAndResidents: async (accountToDelete) => {
    clearToasts();
    try {
      showLoading(true);
      if (accountToDelete.role === Role.ADMIN) {
        stickyToast('danger', 'Error', 'Admin account can not be deleted.')
      } else {
        await deleteUserAccount(accountToDelete.account_id);
        await deleteResidentAccounts(accountToDelete.account_id);
        const updatedAccounts = get(accounts)
          .filter(account => account.account_id !== accountToDelete.account_id);
        accounts.set(updatedAccounts);
        toast('success', 3000, 'Deleted', 'Account was successfully deleted.');
      }
    } catch(e) {
      console.log(e);
      stickyToast('danger', 'Error', 'An error occured while deleting account.');
    } finally {
      showLoading(false);
    }
  },

  getAlertCountsByAccount: async () => {
    const allQueries = [];
    get(accounts)
      .forEach(account => allQueries.push(
        loadReportAlerts(account, moment().subtract(6, 'months').toDate()
      )));
    const results = await Promise.all(allQueries);
    alertCounts.set(addRemainingAlerts(stitchAlertsWithAccount(results)));
  },

  getAlertAreas: async () => {
    return HttpService.send(
      `${API_BASE_URL}/areas`, 
      'GET'
    );
  },

  displayDeleteAccountModal: (display) => {
    ui.update(current => Object.assign(current, {displayDeleteAccountModal: display}));
  },

  displayDeleteResidentModal: (display) => {
    ui.update(current => Object.assign(current, {displayDeleteResidentModal: display}));
  },

  displayTestAlertModal: (display) => {
    ui.update(current => Object.assign(current, {displayTestAlertModal: display}));
  },

  addFilter: filter => { 
    if (!get(alertFilters).some(current => current.type === filter.type && current.value === filter.value)) {
      alertFilters.update(current => [...current, filter]);
      // when adding a filter, unselect all residents
      AppController.setAllResidentsSelectProperty(false);
    } 
  },

  setAllResidentsSelectProperty: selected => {
    const residentsCopy = [...get(residents)];
    const updatedResidentsCopy = residentsCopy.map(resident => ({...resident, selected: selected}));
    residents.set(updatedResidentsCopy);
  },

  removeFilter: filterToRemove => {
    alertFilters.update(current => {
      return current.filter(currentFilter => currentFilter.type !== filterToRemove.type || 
        (currentFilter.type === filterToRemove.type && currentFilter.value !== filterToRemove.value));
    });
    // when removing a filter, unselect all residents
    AppController.setAllResidentsSelectProperty(false);
  },

  ubmaxAccExists: (accNum, residentID) => {
    return get(residents).some(resident => resident.ubmax_acc === accNum && resident.resident_id !== residentID);
  },

  uploadFiles: async (file, accountID) => {
    return await FileUploadService.upload(file, accountID);
  },

  resetPassword: async (email) => {
    return LoginService.reset(email);
  },

  emailResidentRequest: async(resident) => {
    console.log(resident);
    const message = `
    Request for alert service from resident:

    First name: ${resident.firstName}

    Last name: ${resident.lastName}

    Account number: ${resident.account}

    Email: ${resident.email}

    Phone number: ${resident.phoneNumber}

    Cell number: ${resident.cellNumber}

    House number: ${resident.houseNumber}

    Street: ${resident.street}

    Unit: ${resident.unit}

    PO Box: ${resident.poBox}

    Country: ${resident.country}

    Postal Code: ${resident.postalCode}

    Preferred Language: ${resident.preferredLanguage}

    Number of Children: ${resident.numberOfChildren}

    Number of Pets: ${resident.numberOfPets}
    `;

    const subject = 'Public Alert Resident Request';

    return HttpService.send(
      `${API_BASE_URL}/signup`, 
      'POST', 
      {}, 
      JSON.stringify({
        accountId: resident.alertArea,
        subject,
        message
      })
    );
  }
}
